import React from "react";
import PropTypes from "prop-types";
import styled from "styled-components";
import Carousel from "react-multi-carousel";
import { FaArrowLeft, FaArrowRight } from "react-icons/fa";
import { color } from "src/styles/variables";

import "react-multi-carousel/lib/styles.css";

const ProductCarousel = ({ title, children, maxWidth = "100%" }) => (
  <Wrapper data-selector="ProductCarousel">
    {title && <Title data-selector="ProductCarousel-title">{title}</Title>}
    <ProductList>
      <div
        style={{ width: "100%", maxWidth }}
        data-selector="ProductCarousel-list"
      >
        <Carousel
          draggable={false}
          showDots={true}
          swipeable={true}
          arrows={false}
          renderButtonGroupOutside={true}
          keyBoardControl={false}
          customButtonGroup={<ButtonGroup />}
          responsive={{
            tablet: {
              breakpoint: { max: 4096, min: 720 },
              items: 4,
              slidesToSlide: 4,
            },
            mobile: {
              breakpoint: { max: 719, min: 320 },
              items: 2,
              slidesToSlide: 2,
            },
            tiny: {
              breakpoint: { max: 319, min: 0 },
              items: 1,
              slidesToSlide: 1,
            },
          }}
        >
          {children}
        </Carousel>
      </div>
    </ProductList>
  </Wrapper>
);

const ButtonGroup = ({ next, previous, ...rest }) => {
  const {
    carouselState: { currentSlide, totalItems, slidesToShow },
  } = rest;
  const isFirstSlide = currentSlide === 0;
  const isLastSlide = currentSlide >= totalItems - slidesToShow;
  const isSingleSlide = totalItems <= slidesToShow;

  return (
    <CarouselButtonGroup hidden={isSingleSlide}>
      <CarouselArrow
        data-selector="ProductCarousel-previous"
        disabled={isFirstSlide}
        onClick={previous}
      >
        <FaArrowLeft size="24" />
      </CarouselArrow>
      <CarouselArrow
        data-selector="ProductCarousel-next"
        disabled={isLastSlide}
        onClick={next}
      >
        <FaArrowRight size="24" />
      </CarouselArrow>
    </CarouselButtonGroup>
  );
};

ProductCarousel.propTypes = {
  children: PropTypes.any.isRequired,
  title: PropTypes.string,
  maxWidth: PropTypes.number,
  removeBasketLinkInSuccessToast: PropTypes.bool,
};

export default React.memo(ProductCarousel);

const Wrapper = styled.section`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  text-align: center;
`;

const Title = styled.h2`
  font-size: 24px;
  font-weight: 600;
  margin-bottom: 20px;
`;

const ProductList = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  transition: opacity 0.25s ease-out;

  .react-multi-carousel-list {
    padding-bottom: 48px;
    margin-bottom: 20px;
  }

  .react-multi-carousel-track {
    margin: 0 auto;
  }

  .react-multi-carousel-item {
    padding: 0 5px;

    @media (min-width: 390px) {
      padding: 0 10px;
    }
  }

  .react-multi-carousel-dot-list {
    padding: 10px 0;

    li:only-child {
      display: none;
    }
  }

  .react-multi-carousel-dot button {
    background-color: ${color.silverChalice};
    border-color: ${color.silverChalice};
    transition: all 0.2s ease-out;
  }

  .react-multi-carousel-dot button:hover,
  .react-multi-carousel-dot--active button {
    border-color: ${color.grey};
    background-color: ${color.grey};
    transition: all 0.2s ease-out;
  }
`;

const CarouselButtonGroup = styled.div`
  display: none;

  ${({ hidden }) =>
    !hidden &&
    `
  @media (min-width: 720px) {
    display: flex;
    justify-content: space-between;
    width: 100%;
    margin-top: -60px;
  }
  `}
`;

const CarouselArrow = styled.button`
  z-index: 2;
  border: 0;
  margin-top: 5px;
  background: none;
  color: ${color.grey};
  cursor: pointer;
  transition: color 0.2s ease-out;

  &:disabled {
    color: ${color.silverChalice};
  }
`;
